import _ from 'lodash';
import axios from 'axios';
import fromStringWithBreakOrCommaToArray from 'utils/fromStringWithBreakOrCommaToArray';
import { ENUM_POINT_CHANGE_TYPE } from '../utils/checkContents';
import { FAILED_TO_CHECK_DUPLICATE_TUNE_OFFER_ID } from '../../../content';
import {
  OFFER_FIELD_MAPPING,
  OFFER_STATE_PROPS,
  ONETIME_POINTS_CHANGE_TIMEZONE,
  TIMEZONE_OPTIONS_MAP,
  UploadStates,
  ValidationErrorMessages,
} from 'constants/index';
import {
  Offer,
  ZipUploadPayload,
  addNotification,
  checkDuplicateTuneOfferId,
  clearOffers,
  createOffer,
  deleteOffer,
  deleteZipCodes,
  duplicateOffer,
  fetchOffer,
  fetchOffers,
  loadOriginalOffer,
  moveOffer,
  removeFromStates,
  removeOffer,
  saveOffer,
  searchOffers,
  setAllOffers,
  setInitialPlacementOffers,
  setOfferLoadingState,
  setOffersLoadingState,
  setTuneOfferIdError,
  setUIState,
  setZipCount,
  setZipsFileUploadingState,
  toggleDuplicateOfferDialog,
  updateCurrentActiveOffers,
  updateCurrentGroup,
  updateCurrentOffer,
  updateCurrentTargeting,
  updateGroups,
  updateOffers,
  updateStates,
  uploadZipsFile,
} from '../slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { StateNames } from '../validationObjects/states';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { auth } from 'utils/firebase';
import {
  dateTimeZoneToUTC,
  utcToTimeZone,
} from 'utils/dateTimestampConversion';
import { isValidUrl } from '../../../utils/isValidUrl';

const API_ENDPOINT = `${process.env.API_BASE_ADDRESS}/offer-manager/offers`;
function handleCreateOffer(offer: Offer) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.post(API_ENDPOINT, {
      offer: offer,
      user: auth.currentUser?.email,
    });
  });
}

function handleDuplicateOffer(body: any) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.post(API_ENDPOINT + `/duplicate`, body);
  });
}

function handleFetchOffer(offer: Offer) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(API_ENDPOINT + `/${offer.offerid}`);
  });
}

function handleFetchOffers(isActive: boolean, placementId: number) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(
      API_ENDPOINT + `?isactive=${isActive}&placementid=${placementId}`
    );
  });
}

function handleFetchTargetedOffers(body) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(
      `${API_ENDPOINT}/target?${Object.keys(body)
        .map((key) => {
          return key + '=' + body[key];
        })
        .join('&')}`
    );
  });
}

function handleSearchOffers(query: any) {
  const {
    page,
    rowsPerPage,
    search,
    sortOrder,
    sortBy,
    groupId,
    isActive = null,
    category,
  } = query || {};
  const isStatusFilterApplied = _.isNull(isActive) ? false : true;
  const group = groupId === 'all' ? '' : groupId;

  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });

    const categoryParams = new URLSearchParams();
    category.forEach((item: string) => categoryParams.append('category', item));

    const queryParams = new URLSearchParams({
      search: search || '',
      page: page?.toString() || '1',
      rowsPerPage: rowsPerPage?.toString() || '10',
      sortBy: sortBy || '',
      sortOrder: sortOrder || '',
      groupId: group || '',
    });

    if (isStatusFilterApplied) {
      queryParams.append('isactive', isActive);
    }

    category.forEach((item: string) => queryParams.append('category', item));

    return authorizedAxiosInstance.get(
      `${API_ENDPOINT}?${queryParams.toString()}`
    );
  });
}

function handleUpdateOffer(offer: Offer) {
  const filteredOffer = _.omit(offer, ['offerid', 'offergroupid']);
  return auth.currentUser
    .getIdTokenResult()
    .then((res) => {
      const authorizedAxiosInstance = axios.create({
        headers: {
          Authorization: `Bearer ${res.token}`,
        },
      });
      return authorizedAxiosInstance.put(API_ENDPOINT + `/${offer.offerid}`, {
        filteredOffer: filteredOffer,
        user: auth.currentUser?.email,
      });
    })
    .catch((error) => {
      const errorObject = { error };
      if (
        errorObject &&
        errorObject.error &&
        errorObject.error.response &&
        errorObject.error.response.data
      ) {
        throw errorObject.error.response.data;
      }
    });
}

function handleReorderOffers(params) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.post(API_ENDPOINT + `/reorder`, params);
  });
}

function handleDeleteOffer(offer: Offer) {
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.delete(API_ENDPOINT + `/${offer.offerid}`);
  });
}

const handleUploadZipsFile = (data: ZipUploadPayload) => {
  const { file, offerId, userEmail } = data;
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('email', userEmail);
    formData.append('enctype', 'multipart/form-data');
    return authorizedAxiosInstance.post(
      API_ENDPOINT + `/${offerId}/zipsUpload`,
      formData
    );
  });
};

function handleDeleteZipCodes(payload: any) {
  const { offerId, user } = payload;
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.delete(
      API_ENDPOINT + `/${offerId}/zipsUpload?user=${user}`
    );
  });
}

function reformatOfferForAPI(updatedOffer: any, originalOffer?: any) {
  const cloneOfUpdatedOffer = _.clone(updatedOffer);

  // Remove deprecated fields from payload
  delete cloneOfUpdatedOffer.points;
  delete cloneOfUpdatedOffer.onofferseenurltofire;
  delete cloneOfUpdatedOffer.price;
  delete cloneOfUpdatedOffer.comparisonemoji;

  if (!cloneOfUpdatedOffer.isactive) {
    cloneOfUpdatedOffer.isactive = 0;
  }
  if (!cloneOfUpdatedOffer.ctaurl) {
    cloneOfUpdatedOffer.ctaurl = null;
    cloneOfUpdatedOffer.tuneofferid = null;
  }
  if (!cloneOfUpdatedOffer.zindex) {
    cloneOfUpdatedOffer.zindex = null;
  }
  const remainingSuccessUrls = cloneOfUpdatedOffer.successurl
    .filter((x) => x)
    .map((x) => x.split(' ').join(''));
  if (remainingSuccessUrls.length) {
    cloneOfUpdatedOffer.successurl = JSON.stringify(remainingSuccessUrls);
  } else {
    cloneOfUpdatedOffer.successurl = null;
  }
  cloneOfUpdatedOffer.zips = fromStringWithBreakOrCommaToArray(
    cloneOfUpdatedOffer.zips
  );
  cloneOfUpdatedOffer.states = fromStringWithBreakOrCommaToArray(
    cloneOfUpdatedOffer.states
  );
  if (cloneOfUpdatedOffer.states) {
    cloneOfUpdatedOffer.states = cloneOfUpdatedOffer.states.map(
      (name: string) => {
        if (StateNames[name]) return StateNames[name];
        return name;
      }
    );
  }
  if (cloneOfUpdatedOffer.devices === 'ALL_DEVICES') {
    cloneOfUpdatedOffer.devices = null;
  }
  if (
    cloneOfUpdatedOffer.enddate &&
    typeof cloneOfUpdatedOffer.enddate !== 'number'
  ) {
    cloneOfUpdatedOffer.enddate = Date.parse(cloneOfUpdatedOffer.enddate);
  }
  if (
    cloneOfUpdatedOffer.startdate &&
    typeof cloneOfUpdatedOffer.startdate !== 'number'
  ) {
    cloneOfUpdatedOffer.startdate = Date.parse(cloneOfUpdatedOffer.startdate);
  }
  if (cloneOfUpdatedOffer.logourl) {
    cloneOfUpdatedOffer.logourl = cloneOfUpdatedOffer.logourl
      .split(' ')
      .join('');
  }
  if (cloneOfUpdatedOffer.ctaurl) {
    cloneOfUpdatedOffer.ctaurl = cloneOfUpdatedOffer.ctaurl.split(' ').join('');
  }
  if (cloneOfUpdatedOffer.mediarate) {
    cloneOfUpdatedOffer.mediarate = parseFloat(cloneOfUpdatedOffer.mediarate);
  }
  if (cloneOfUpdatedOffer.backgroundimgurl) {
    cloneOfUpdatedOffer.backgroundimgurl = cloneOfUpdatedOffer.backgroundimgurl
      .split(' ')
      .join('');
  }
  if (cloneOfUpdatedOffer.audiences) {
    cloneOfUpdatedOffer.audiences = cloneOfUpdatedOffer.audiences.map(
      ({ audienceid }) => audienceid
    );
  }

  if (!cloneOfUpdatedOffer.fullscreenimageurl) {
    cloneOfUpdatedOffer.fullscreenimageurl = null;
  }

  if (cloneOfUpdatedOffer.offertunegoals?.length > 0) {
    cloneOfUpdatedOffer.offertunegoals = cloneOfUpdatedOffer.offertunegoals.map(
      (goal) => {
        const currentGoal = JSON.parse(JSON.stringify(goal));

        const propToNumber = [
          'goalid',
          'goalcompletionorder',
          'conversiontimerange',
          'conversioncapvalue',
          'pointsawarded',
          'pointschangestartvalue',
          'pointschangeendvalue',
        ];
        for (const key of propToNumber) {
          if (currentGoal[key]) currentGoal[key] = Number(currentGoal[key]);
        }

        if (currentGoal.ispointschangescheduled) {
          if (currentGoal.pointschangestartdate) {
            currentGoal.pointschangestartdate = dateTimeZoneToUTC(
              currentGoal.pointschangestartdate,
              currentGoal.pointschangestimezone
            );
          }

          currentGoal.pointschangestartvalue = Number(
            currentGoal.pointschangestartvalue
          );

          if (currentGoal.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO) {
            if (currentGoal.pointschangeenddate) {
              currentGoal.pointschangeenddate = dateTimeZoneToUTC(
                currentGoal.pointschangeenddate,
                currentGoal.pointschangestimezone
              );
            }

            currentGoal.pointschangeendvalue = Number(
              currentGoal.pointschangeendvalue
            );
          } else {
            currentGoal.pointschangeenddate = null;
            currentGoal.pointschangeendvalue = null;
          }
        } else {
          currentGoal.pointschangetype = null;
          currentGoal.pointschangestartdate = null;
          currentGoal.pointschangestartvalue = null;
          currentGoal.pointschangeenddate = null;
          currentGoal.pointschangeendvalue = null;
          currentGoal.pointschangestimezone = null;
        }

        if (currentGoal?.oldconversiontimerange) {
          if (
            currentGoal?.oldconversiontimerange !==
            currentGoal?.conversiontimerange
          ) {
            currentGoal.currentconversionvalue =
              currentGoal?.isconversioncapactive ? 0 : null;
          }

          delete currentGoal.oldconversiontimerange;
        }

        if (!currentGoal?.isconversioncapactive) {
          return {
            ...currentGoal,
            currentconversionvalue: null,
            conversiontimerange: null,
            conversioncapvalue: null,
          };
        }

        if (
          currentGoal?.isconversioncapactive &&
          !currentGoal.currentconversionvalue
        ) {
          return { ...currentGoal, currentconversionvalue: 0 };
        }

        if (!currentGoal.goalcompletioninstructions) {
          currentGoal.goalcompletioninstructions = null;
        }
        return currentGoal;
      }
    );
  }

  if (cloneOfUpdatedOffer.category?.length === 0) {
    cloneOfUpdatedOffer.category = [];
  }

  if (cloneOfUpdatedOffer.ispointschangescheduled) {
    if (cloneOfUpdatedOffer.pointschangestartdate) {
      cloneOfUpdatedOffer.pointschangestartdate = dateTimeZoneToUTC(
        cloneOfUpdatedOffer.pointschangestartdate,
        cloneOfUpdatedOffer.pointschangestimezone
      );
    }

    cloneOfUpdatedOffer.pointschangestartvalue = Number(
      cloneOfUpdatedOffer.pointschangestartvalue
    );

    if (cloneOfUpdatedOffer.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO) {
      if (cloneOfUpdatedOffer.pointschangeenddate) {
        cloneOfUpdatedOffer.pointschangeenddate = dateTimeZoneToUTC(
          cloneOfUpdatedOffer.pointschangeenddate,
          cloneOfUpdatedOffer.pointschangestimezone
        );
      }

      cloneOfUpdatedOffer.pointschangeendvalue = Number(
        cloneOfUpdatedOffer.pointschangeendvalue
      );
    } else {
      cloneOfUpdatedOffer.pointschangeenddate = null;
      cloneOfUpdatedOffer.pointschangeendvalue = null;
    }
  } else {
    cloneOfUpdatedOffer.pointschangetype = null;
    cloneOfUpdatedOffer.pointschangestartdate = null;
    cloneOfUpdatedOffer.pointschangestartvalue = null;
    cloneOfUpdatedOffer.pointschangeenddate = null;
    cloneOfUpdatedOffer.pointschangeendvalue = null;
    cloneOfUpdatedOffer.pointschangestimezone = null;
  }

  // Handle isactiveinapp assignment based on create vs update case
  if (!originalOffer) {
    // Create offer case: originalOffer is null/undefined
    cloneOfUpdatedOffer.isactiveinapp = cloneOfUpdatedOffer.isactive;
  } else {
    // Update offer case: only update isactiveinapp if isactive changed
    // Compare originalOffer.isactive with the updated offer's isactive
    if (
      originalOffer.isactive !== undefined &&
      cloneOfUpdatedOffer.isactive !== undefined &&
      originalOffer.isactive !== cloneOfUpdatedOffer.isactive
    ) {
      cloneOfUpdatedOffer.isactiveinapp = cloneOfUpdatedOffer.isactive;
    }
  }
  return cloneOfUpdatedOffer;
}

function reformatOfferForFrontend(fetchedOffer: any) {
  const cloneOfFetchedOffer = _.clone(fetchedOffer);
  if (!cloneOfFetchedOffer.audiences) {
    cloneOfFetchedOffer.audiences = [];
  }
  if (cloneOfFetchedOffer.successurl) {
    const countSuccessUrls = (
      cloneOfFetchedOffer.successurl.match(/http/g) || []
    ).length;
    const isArray = cloneOfFetchedOffer.successurl[0] === '[';
    if (countSuccessUrls === 1 && !isArray) {
      cloneOfFetchedOffer.successurl = [cloneOfFetchedOffer.successurl];
    } else {
      cloneOfFetchedOffer.successurl = JSON.parse(
        cloneOfFetchedOffer.successurl
      );
    }
  } else {
    cloneOfFetchedOffer.successurl = [null];
  }

  if (cloneOfFetchedOffer.zips && cloneOfFetchedOffer.zips.length) {
    cloneOfFetchedOffer.zips = cloneOfFetchedOffer.zips.join('\n');
  } else {
    cloneOfFetchedOffer.zips = '';
  }
  if (cloneOfFetchedOffer.states && cloneOfFetchedOffer.states.length) {
    cloneOfFetchedOffer.states = cloneOfFetchedOffer.states.join('\n');
  } else {
    cloneOfFetchedOffer.states = '';
  }
  if (!cloneOfFetchedOffer.devices) {
    cloneOfFetchedOffer.devices = 'ALL_DEVICES';
  }
  if (!cloneOfFetchedOffer.pricingmodel) {
    cloneOfFetchedOffer.pricingmodel = '';
  }

  if (!cloneOfFetchedOffer.revenueconversionevent) {
    cloneOfFetchedOffer.revenueconversionevent = '';
  }
  if (!cloneOfFetchedOffer.zipCount) {
    cloneOfFetchedOffer.zipCount = 0;
  }
  if (!cloneOfFetchedOffer.zip_upload_status) {
    cloneOfFetchedOffer.zip_upload_status = UploadStates.DONE;
  }

  if (cloneOfFetchedOffer.category?.length === 0) {
    cloneOfFetchedOffer.category = [];
  }

  if (cloneOfFetchedOffer.placementid?.length === 0) {
    cloneOfFetchedOffer.placementid = ['1'];
  }

  if (!cloneOfFetchedOffer.offertunegoals) {
    const newGoal = {
      goalid: 0,
      pointsawarded: undefined,
      goalcompletioninstructions: '',
      status: 'insert',
    };
    cloneOfFetchedOffer.offertunegoals = [];
    cloneOfFetchedOffer.offertunegoals.push(newGoal, newGoal);
  } else {
    for (const goal of cloneOfFetchedOffer.offertunegoals) {
      if (goal.pointschangestartdate) {
        goal.pointschangestartdate = utcToTimeZone(
          goal.pointschangestartdate,
          goal.pointschangestimezone
        );
      }
      if (goal.pointschangeenddate) {
        goal.pointschangeenddate = utcToTimeZone(
          goal.pointschangeenddate,
          goal.pointschangestimezone
        );
      }
    }
  }

  if (!cloneOfFetchedOffer.ispointschangescheduled) {
    cloneOfFetchedOffer.ispointschangescheduled = 0;
  }

  if (cloneOfFetchedOffer.pointschangestartdate) {
    cloneOfFetchedOffer.pointschangestartdate = utcToTimeZone(
      cloneOfFetchedOffer.pointschangestartdate,
      cloneOfFetchedOffer.pointschangestimezone
    );
  }

  if (cloneOfFetchedOffer.pointschangeenddate) {
    cloneOfFetchedOffer.pointschangeenddate = utcToTimeZone(
      cloneOfFetchedOffer.pointschangeenddate,
      cloneOfFetchedOffer.pointschangestimezone
    );
  }

  return cloneOfFetchedOffer;
}

function* watchCreateOffer(action: PayloadAction<any>) {
  const { updatedOffer, currentGroup } = action.payload;
  try {
    yield put(setOfferLoadingState({ state: 'loading' }));
    const offerForAPI = reformatOfferForAPI(updatedOffer, null);
    const result = yield call(handleCreateOffer, offerForAPI);
    const offer = result.data;
    const offerid = result.data.offerid;
    window.history.pushState(
      null,
      document.title,
      `${window.location.href}/${offerid}`
    );
    const offerForFrontend = reformatOfferForFrontend(offer);
    yield put(loadOriginalOffer(offerForFrontend));
    yield put(updateCurrentOffer(offerForFrontend));
    yield put(updateOffers(offerForFrontend));
    const updatedGroup = {
      ...currentGroup,
      offers: _.union(currentGroup.offers, [offerForFrontend.offerid]),
    };
    yield put(updateCurrentGroup(updatedGroup));
    yield put(updateGroups(updatedGroup));
    yield put(setOfferLoadingState({ state: 'done' }));
    // Reset tune offer ID validation state
    yield put(setTuneOfferIdError({ message: null, duplicateOfferIds: [] }));
    yield put(
      addNotification({
        state: 'done',
        message: `Successfully Created Offer ${offerForFrontend.offername}`,
      })
    );
  } catch (e) {
    const { updatedOffer } = action.payload;
    yield put(setOfferLoadingState({ state: 'done' }));
    yield put(
      addNotification({
        state: 'error',
        message: `Failed to Create Offer ${updatedOffer.offername}`,
      })
    );
  }
}

// Helper function to check for duplicate tune offer IDs
function* checkForDuplicateTuneOfferId(offer: Offer) {
  try {
    yield put(
      setUIState({
        value: true,
        uiPropertyName: OFFER_STATE_PROPS.isTuneGoalIdValidating,
      })
    );

    if (!offer.ctaurl) {
      yield put(setTuneOfferIdError({ message: null, duplicateOfferIds: [] }));
      yield put(
        setUIState({
          value: false,
          uiPropertyName: OFFER_STATE_PROPS.isTuneGoalIdValidating,
        })
      );
      return;
    }

    const urlValidation = isValidUrl(
      offer.ctaurl,
      OFFER_FIELD_MAPPING.clickthrough_url,
      ['offer_id']
    );
    if (urlValidation.isValid) {
      // Check for duplicate tune offer ID
      const duplicateCheckResult = yield call(handleCheckDuplicateTuneOfferId, {
        ctaurl: offer.ctaurl,
        offerId: offer?.offerid || '',
      });
      if (!duplicateCheckResult.data.error) {
        yield put(
          setTuneOfferIdError({ message: null, duplicateOfferIds: [] })
        );
      } else {
        yield put(
          setTuneOfferIdError({
            message: duplicateCheckResult.data.message,
            duplicateOfferIds:
              duplicateCheckResult.data.duplicateOfferIds || [],
          })
        );
      }
    }

    yield put(
      setUIState({
        value: false,
        uiPropertyName: OFFER_STATE_PROPS.isTuneGoalIdValidating,
      })
    );
  } catch (e) {
    console.error(e);
    yield put(
      setUIState({
        value: false,
        uiPropertyName: OFFER_STATE_PROPS.isTuneGoalIdValidating,
      })
    );

    yield put(
      setTuneOfferIdError({
        message: ValidationErrorMessages.URL_INVALID,
        duplicateOfferIds: [],
      })
    );
  }
}

function* watchFetchOffer(action: PayloadAction<Offer>) {
  try {
    yield put(loadOriginalOffer(undefined));
    yield put(setOfferLoadingState({ state: 'loading' }));
    const offer = action.payload;
    yield put(updateCurrentOffer(offer));
    const result = yield call(handleFetchOffer, offer);
    const fetchedOffer = result.data;
    const offerForFrontend = reformatOfferForFrontend(fetchedOffer);

    // Check for duplicate tune offer ID if ctaurl exists
    yield call(checkForDuplicateTuneOfferId, offerForFrontend);

    yield put(updateCurrentOffer(offerForFrontend));
    yield put(updateOffers(offerForFrontend));
    yield put(loadOriginalOffer(offerForFrontend));
    yield put(setOfferLoadingState({ state: 'done' }));
  } catch (e) {
    yield put(
      setUIState({
        value: true,
        uiPropertyName: 'errorFetching',
      })
    );
    yield put(setOfferLoadingState({ state: 'done' }));
    yield put(
      addNotification({
        state: 'error',
        message: `Failed to Retrieve Offer ${action.payload.offername}`,
      })
    );
  }
}

function* watchFetchOffers(
  action: PayloadAction<{ isActive: boolean; placementId: number }>
) {
  try {
    yield put(setOffersLoadingState({ state: 'loading' }));

    const { isActive, placementId } = action.payload;
    const result = yield call(handleFetchOffers, isActive, placementId);
    const modifiedOffers = result.data
      .filter((offer) => {
        const timestamp = Date.now();
        if (offer.startdate && offer.startdate > timestamp) return false;
        if (offer.enddate && offer.enddate < timestamp) return false;
        return true;
      })
      .map(reformatOfferForFrontend);
    yield put(updateOffers(modifiedOffers));
    yield put(updateCurrentActiveOffers(modifiedOffers.map((x) => x.offerid)));
    yield put(setOffersLoadingState({ state: 'done' }));
  } catch (e) {
    yield put(setOffersLoadingState({ state: 'done' }));
  }
}

function* watchUploadZipsFile(action: PayloadAction<ZipUploadPayload>) {
  try {
    const data = action.payload;
    yield call(handleUploadZipsFile, data);
  } catch (e) {
    console.error(e);
    yield put(
      addNotification({
        state: 'error',
        message: `Failed to upload ${action.payload.file.name}`,
      })
    );
    yield put(setZipsFileUploadingState(UploadStates.DONE));
  }
}

function* watchDeleteZipCodes(action: PayloadAction<{ offerId: number }>) {
  try {
    const result = yield call(handleDeleteZipCodes, action.payload);
    if (result.status === 200) {
      yield put(setZipCount(0));
    }
  } catch (err) {
    console.error(err);
    yield put(
      addNotification({ state: 'error', message: 'Failed to delete zipCodes' })
    );
  }
}

function* watchUpdateOffer(action: { payload: { offername: any } }) {
  try {
    yield put(setOfferLoadingState({ state: 'loading' }));
    const offer = action.payload;
    const originalOfferSelector = (state: any) =>
      state.offerWallReducer.originalOffer;
    const originalOffer = yield select(originalOfferSelector);
    const offerForAPI = reformatOfferForAPI(offer, originalOffer);

    const result = yield call(handleUpdateOffer, offerForAPI);
    const fetchedOffer = result.data;
    const offerForFrontend = reformatOfferForFrontend(fetchedOffer);
    yield put(updateCurrentOffer(offerForFrontend));
    yield put(updateOffers(offerForFrontend));
    yield put(loadOriginalOffer(offerForFrontend));
    yield put(setOfferLoadingState({ state: 'done' }));
    // Reset tune offer ID validation state
    yield put(setTuneOfferIdError({ message: null, duplicateOfferIds: [] }));

    if (fetchedOffer.errors?.length) {
      const errorMessage = fetchedOffer.errors.join('<br/>');
      yield put(
        addNotification({
          state: 'error',
          message: errorMessage,
        })
      );
    } else {
      yield put(
        addNotification({
          state: 'done',
          message: `Successfully Updated Offer ${offerForAPI.offername}`,
        })
      );
    }
  } catch (e) {
    let errorMessage = null;
    if (e.errors && e.errors.length)
      errorMessage = e.errors.map((error) => error.message).join('<br/>');
    yield put(
      addNotification({
        state: 'error',
        message: `Failed to Update Offer ${action.payload.offername}${
          errorMessage && errorMessage.length ? `<br/>${errorMessage}` : null
        }`,
      })
    );
    yield put(setOfferLoadingState({ state: 'done' }));
  }
}

function* watchDeleteOffer(action) {
  try {
    yield put(updateStates(action.payload.offerid));
    yield call(handleDeleteOffer, action.payload);
    yield put(removeOffer(action.payload));
    yield put(removeFromStates(action.payload.offerid));
    yield put(
      addNotification({
        state: 'done',
        message: `Successfully Deleted Offer ${action.payload.offername}`,
      })
    );
  } catch (e) {
    yield put(removeFromStates(action.payload.offerid));
    yield put(
      addNotification({
        state: 'error',
        message: `Failed to Delete Offer ${action.payload.offername}`,
      })
    );
  }
}

function* updateOffersNoTargeting() {
  yield put(clearOffers());
  const fetchOfferPromises = [1, 2, 3, 4].map((placementid) =>
    call(handleFetchOffers, true, placementid)
  );
  const fetchOfferPromisesResults = yield all(fetchOfferPromises);
  let offers = [];
  for (const result of fetchOfferPromisesResults) {
    const { data } = result;
    if (data && data.length) {
      offers = offers.concat(data);
    }
  }
  for (const offer of offers) {
    yield put(updateOffers(offer));
  }
  yield put(setAllOffers(offers));
  const offerids = offers.map((offer) => offer.offerid);
  yield put(updateCurrentActiveOffers(offerids));
}

function* watchMoveOffer(action) {
  try {
    const { offerids, placementid, alloffers } = action.payload;

    yield call(handleReorderOffers, { offerids, placementid });
    const offers = alloffers.map((item) => {
      if (item.placementid === placementid && offerids.includes(item.offerid)) {
        return {
          ...item,
          offerorder: offerids.indexOf(item.offerid),
        };
      }
      return item;
    });
    yield put(setAllOffers(offers));
  } catch (e) {
    console.log(e);
  }
}

function reformatCurrentTargetingForAPI(object) {
  const cloneOfObject = _.clone(object);
  if (cloneOfObject.device == 'None') cloneOfObject.device = '';
  if (cloneOfObject.zip) cloneOfObject.zip = parseInt(cloneOfObject.zip);
  return cloneOfObject;
}

function* watchDuplicateOffer(action) {
  const { offerToDuplicate, offerGroupId, groupName, ctaurl } = action.payload;
  const body = {};
  body.offerid = offerToDuplicate.offerid;
  if (offerGroupId) {
    body.groupid = offerGroupId;
  } else {
    body.groupid = offerToDuplicate.offergroupid;
  }
  if (groupName) {
    delete body.groupid;
    body.groupname = groupName;
  }
  if (ctaurl) {
    body.ctaurl = ctaurl;
  }
  yield put(toggleDuplicateOfferDialog({ open: false }));
  try {
    yield put(setOfferLoadingState({ state: 'loading' }));
    const result = yield call(handleDuplicateOffer, body);
    const copiedOffer = result.data;
    const newOfferId = copiedOffer.offerid;
    window.history.pushState(
      null,
      document.title,
      `/main/offer-wall-manager/editor/${newOfferId}`
    );
    const offerForFrontend = reformatOfferForFrontend(copiedOffer);
    yield put(updateCurrentOffer(offerForFrontend));
    yield put(updateOffers(offerForFrontend));
    yield put(loadOriginalOffer(offerForFrontend));
    yield put(setOfferLoadingState({ state: 'done' }));
    yield put(
      addNotification({
        state: 'done',
        message: `Successfully Duplicated Offer ${offerToDuplicate.label}`,
      })
    );
  } catch (e) {
    console.log('Error:', e);
    yield put(setOfferLoadingState({ state: 'error' }));
    yield put(
      addNotification({
        state: 'error',
        message: `Failed to Duplicate Offer ${offerToDuplicate.label}`,
      })
    );
  }
}

function* watchUpdateCurrentTargeting() {
  try {
    yield put(setOffersLoadingState({ state: 'loading' }));
    const currentTargetingFunction = (state) =>
      state.offerWallReducer.currentTargeting;
    const currentTargeting = yield select(currentTargetingFunction);
    let offers = [];
    if (_.isEmpty(currentTargeting)) {
      yield call(updateOffersNoTargeting);
    } else {
      yield put(clearOffers());
      const reformattedObject =
        reformatCurrentTargetingForAPI(currentTargeting);
      const results = yield call(handleFetchTargetedOffers, reformattedObject);
      offers = results.data;
      for (const offer of offers) {
        yield put(updateOffers(offer));
      }
      yield put(setAllOffers(offers));
      const offerids = offers.map((offer) => offer.offerid);
      yield put(updateCurrentActiveOffers(offerids));
    }
  } catch (e) {
    console.log(e);
  }
  yield put(setOffersLoadingState({ state: 'done' }));
}

function* watchSearchOffers(action) {
  try {
    yield put(setOffersLoadingState({ state: 'loading' }));
    const result = yield call(handleSearchOffers, action.payload);
    const offers = result.data;
    yield put(updateOffers(offers));
    yield put(setOffersLoadingState({ state: 'done' }));
  } catch (e) {
    yield put(
      addNotification({
        state: 'error',
        message: `Error while fetching Offers`,
      })
    );
    yield put(setOffersLoadingState({ state: 'error' }));
  }
}

function* watchSetInitialPlacementOffers() {
  try {
    yield put(setOffersLoadingState({ state: 'loading' }));
    yield call(updateOffersNoTargeting);
  } catch (e) {
    console.log(e);
  }
  yield put(setOffersLoadingState({ state: 'done' }));
}

function handleCheckDuplicateTuneOfferId(payload: {
  ctaurl: string;
  offerId: number;
}) {
  const { ctaurl, offerId } = payload;
  return auth.currentUser.getIdTokenResult().then((res) => {
    const authorizedAxiosInstance = axios.create({
      headers: {
        Authorization: `Bearer ${res.token}`,
      },
    });
    return authorizedAxiosInstance.get(
      `${API_ENDPOINT}/validate-tuneofferid?clickthroughUrl=${ctaurl}&offerId=${offerId}`
    );
  });
}

function* watchCheckDuplicateTuneOfferId(action: any) {
  yield call(checkForDuplicateTuneOfferId, action.payload);
}

export default function* watchAll() {
  yield takeLatest<any>(moveOffer.type, watchMoveOffer);
  yield takeLatest<any>(createOffer.type, watchCreateOffer);
  yield takeLatest<any>(fetchOffer.type, watchFetchOffer);
  yield takeLatest<any>(fetchOffers.type, watchFetchOffers);
  yield takeLatest<any>(saveOffer.type, watchUpdateOffer);
  yield takeLatest<any>(searchOffers.type, watchSearchOffers);
  yield takeLatest<any>(deleteOffer.type, watchDeleteOffer);
  yield takeLatest<any>(duplicateOffer.type, watchDuplicateOffer);
  yield takeLatest<any>(
    updateCurrentTargeting.type,
    watchUpdateCurrentTargeting
  );
  yield takeLatest<any>(
    setInitialPlacementOffers.type,
    watchSetInitialPlacementOffers
  );
  yield takeLatest<any>(uploadZipsFile.type, watchUploadZipsFile);
  yield takeLatest<any>(deleteZipCodes.type, watchDeleteZipCodes);
  yield takeLatest<any>(
    checkDuplicateTuneOfferId.type,
    watchCheckDuplicateTuneOfferId
  );
}
